import { formErrorsFromFields, type FormErrors } from '@/lib/form-errors';
import type { KeyValueRow } from '@/lib/key-value';
import { esZonaHorariaValida } from '@/lib/timezone';

/**
 * Validación en el cliente de los formularios de Ajustes.
 *
 * Espejo de las reglas reales del backend (módulo 2):
 *  - `backend/src/modules/settings/settings.schemas.ts`
 *  - `backend/src/modules/tenants/tenants.schemas.ts`
 *  - `backend/src/modules/payment-methods/payment-methods.schemas.ts`
 *
 * Son las mismas reglas y los mismos mensajes, para que el error salga debajo del campo sin
 * esperar al viaje al servidor (el backend ya devuelve el campo de cada mensaje en
 * `error.details`, ver `src/lib/form-errors.ts`).
 *
 * El backend sigue siendo la autoridad: si cambia una regla allí y el cliente no la conoce, su
 * mensaje se pinta igual (bajo el campo o en el aviso general, `FormErrorAlert`).
 */

/** Topes tomados de los schemas Zod del backend. */
export const LIMITS = {
  nombreAsistente: 120,
  descripcionEmpresa: 2000,
  politicas: 5000,
  personalidadTono: 2000,
  adminWhatsapp: 30,
  regla: 500,
  reglas: 50,
  pais: 60,
  moneda: 10,
  zonaHoraria: 60,
  promptPropio: 8000,
  // «Foto que no está en el catálogo» (23-sep-2026): el texto que recibe el cliente.
  mensajeFotoNoCatalogo: 1000,
  nombreEmpresa: 150,
  paymentNombre: 120,
  paymentTipo: 60,
  paymentCondiciones: 1000,
} as const;

/* ---------------------------------------------------------------------------
 * Genéricos
 * ------------------------------------------------------------------------- */

/** `undefined` = sin error. */
export function validateMaxLength(value: string, max: number, label: string): string | undefined {
  if (value.trim().length > max) return `${label} no puede superar los ${max} caracteres`;
  return undefined;
}

/** Campo obligatorio con tope (`z.string().min(1).max(n)`). */
export function validateRequired(
  value: string,
  max: number,
  label: string,
  emptyMessage: string,
): string | undefined {
  if (!value.trim()) return emptyMessage;
  return validateMaxLength(value, max, label);
}

/* ---------------------------------------------------------------------------
 * Negocio (PATCH /api/tenants/:tenantId)
 * ------------------------------------------------------------------------- */

/** Nombre del negocio: obligatorio, ≤ 150. */
export function validateBusinessName(value: string): string | undefined {
  return validateRequired(
    value,
    LIMITS.nombreEmpresa,
    'El nombre del negocio',
    'El nombre del negocio es obligatorio',
  );
}

/**
 * Logo: opcional; si se escribe debe ser una URL válida.
 *
 * Espejo de `logoUrlSchema` (`backend/src/modules/tenants/tenants.schemas.ts`), que acepta las dos
 * formas que puede guardar el panel: una URL `http(s)` (lo que se pega a mano) o la ruta relativa
 * que devuelve `POST /api/tenants/:tenantId/logo` cuando el backend NO tiene `PUBLIC_BASE_URL`
 * (`/uploads/logos/<tenantId>/logo.png`). El mensaje es el mismo que devuelve el backend.
 */
export function validateLogoUrl(value: string): string | undefined {
  const url = value.trim();
  if (!url) return undefined;
  if (!/^(https?:\/\/\S+|\/uploads\/\S+)$/i.test(url)) {
    return 'El logo debe ser una URL válida';
  }
  return undefined;
}

/**
 * Una imagen de la guía de tallas (24-sep-2026): mismo criterio que `validateLogoUrl` — una URL `http(s)`
 * o la ruta relativa `/uploads/...` que devuelve la subida (espejo de `urlImagenSchema` del backend).
 */
export function validateGuiaTallasUrl(value: string): string | undefined {
  const url = value.trim();
  if (!url) return undefined;
  if (!/^(https?:\/\/\S+|\/uploads\/\S+)$/i.test(url)) {
    return 'La guía de tallas debe ser una URL válida (o la ruta /uploads/... que devuelve la subida)';
  }
  return undefined;
}

/**
 * Cuántas imágenes admite la guía de tallas del negocio (24-sep-2026, tarde: eran 1). El tope está también
 * en el schema del backend (`guiaTallasFotos`), así que un `PATCH` a mano con 3 responde 400.
 */
export const GUIA_TALLAS_MAX_FOTOS = 2;

/**
 * La lista completa de la guía de tallas: hasta `GUIA_TALLAS_MAX_FOTOS` imágenes y todas válidas.
 *
 * La lista VACÍA es válida a propósito: quitar la guía es una acción legítima («ya no vendo por tallas»)
 * y el bot vuelve a contestar esas preguntas con el catálogo.
 */
export function validateGuiaTallasFotos(fotos: string[]): string | undefined {
  const validas = fotos.map((foto) => foto.trim()).filter((foto) => foto.length > 0);

  if (validas.length > GUIA_TALLAS_MAX_FOTOS) {
    return `La guía de tallas admite hasta ${GUIA_TALLAS_MAX_FOTOS} imágenes`;
  }

  for (const url of validas) {
    const error = validateGuiaTallasUrl(url);
    if (error) return error;
  }

  return undefined;
}

/* ---------------------------------------------------------------------------
 * Ajustes (PATCH /api/tenants/:tenantId/settings)
 * ------------------------------------------------------------------------- */

export function validateAssistantName(value: string, label: string): string | undefined {
  return validateMaxLength(value, LIMITS.nombreAsistente, label);
}

export function validateDescription(value: string): string | undefined {
  return validateMaxLength(value, LIMITS.descripcionEmpresa, 'La descripción');
}

export function validatePolicies(value: string): string | undefined {
  return validateMaxLength(value, LIMITS.politicas, 'Las políticas');
}

export function validateTone(value: string): string | undefined {
  return validateMaxLength(value, LIMITS.personalidadTono, 'La personalidad');
}

/**
 * Número de WhatsApp del administrador: el backend solo limita la longitud (no valida
 * formato ni país), así que aquí tampoco se inventa una regla.
 */
export function validateAdminWhatsapp(value: string): string | undefined {
  return validateMaxLength(value, LIMITS.adminWhatsapp, 'El número de WhatsApp');
}

/* ---------------------------------------------------------------------------
 * Contexto del negocio (país, moneda y zona horaria) — 22-sep-2026
 * ------------------------------------------------------------------------- */

/**
 * País del negocio: texto libre (el backend usa su nombre para armar el indicativo del teléfono y la
 * validación «el departamento corresponde a la ciudad»), así que solo se comprueba el tope.
 */
export function validateCountry(value: string): string | undefined {
  return validateMaxLength(value, LIMITS.pais, 'El país');
}

/** Moneda: texto libre (`COP`, `MXN`, `EUR`…). Con ella el bot habla de precios. */
export function validateCurrency(value: string): string | undefined {
  return validateMaxLength(value, LIMITS.moneda, 'La moneda');
}

/**
 * Zona horaria IANA: mismo mensaje que el backend (`settings.schemas.ts`). Se comprueba con `Intl`
 * porque el valor viaja al prompt (la hora que ve el modelo): sin esta comprobación, un typo
 * (`America/Bogata`) hacía saltar `RangeError` en cada respuesta del bot.
 */
export function validateTimezone(value: string): string | undefined {
  const zona = value.trim();
  if (!zona) return undefined;
  if (zona.length > LIMITS.zonaHoraria) {
    return `La zona horaria no puede superar los ${LIMITS.zonaHoraria} caracteres`;
  }
  return esZonaHorariaValida(zona) ? undefined : 'Zona horaria no válida (ej. America/Bogota)';
}

/**
 * Instrucciones propias del negocio (`promptVentas` / `promptPostventa`, 22-sep-2026).
 *
 * Tope de 8.000 caracteres y —lo importante— **sin llaves**: los `{{placeholders}}` los llena el backend
 * al armar el prompt. Escribirlas a mano no sustituye nada (el backend las borra) y confunde al dueño,
 * así que se avisa antes de guardar con el mismo mensaje que devuelve el backend.
 */
export function validateOwnPrompt(value: string, label: string): string | undefined {
  const problema = validateMaxLength(value, LIMITS.promptPropio, label);
  if (problema) return problema;
  if (/[{}]/.test(value)) return 'Quita las llaves { }: las variables del sistema no se editan a mano';
  return undefined;
}

/** Reglas generales: hasta 50 reglas de 500 caracteres (las filas vacías se descartan al enviar). */
export function validateRules(rules: string[]): string | undefined {
  if (rules.length > LIMITS.reglas) return `Puedes definir hasta ${LIMITS.reglas} reglas`;

  const tooLong = rules.some((rule) => rule.trim().length > LIMITS.regla);
  if (tooLong) return `Cada regla puede tener hasta ${LIMITS.regla} caracteres`;

  return undefined;
}


/* ---------------------------------------------------------------------------
 * Métodos de pago
 * ------------------------------------------------------------------------- */

export interface PaymentMethodValidationInput {
  nombre: string;
  tipo: string;
  condiciones: string;
  /** Filas de `datos` (JSON libre). `datos` es opcional en el backend. */
  datos: KeyValueRow[];
}

/**
 * Valida el formulario de un método de pago.
 * `datos` es opcional; solo se avisa del caso que siempre es un error: escribir un valor sin
 * su clave (esa fila se descartaría al enviar y el usuario no entendería por qué).
 */
export function validatePaymentMethod(input: PaymentMethodValidationInput): FormErrors {
  const valueWithoutKey = input.datos.some(
    (row) => !row.key.trim() && row.value.trim().length > 0,
  );

  return formErrorsFromFields({
    nombre: validateRequired(
      input.nombre,
      LIMITS.paymentNombre,
      'El nombre',
      'El nombre es obligatorio',
    ),
    tipo: validateRequired(input.tipo, LIMITS.paymentTipo, 'El tipo', 'El tipo es obligatorio'),
    condiciones: validateMaxLength(input.condiciones, LIMITS.paymentCondiciones, 'Las condiciones'),
    datos: valueWithoutKey ? 'Cada valor necesita su clave' : undefined,
  });
}

/* ---------------------------------------------------------------------------
 * Equipo
 * ------------------------------------------------------------------------- */

/** Email de un miembro (debe ser un usuario ya registrado; eso lo comprueba el backend). */
export function validateMemberEmail(value: string): string | undefined {
  const email = value.trim();
  if (!email) return 'El email es obligatorio';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Email inválido';
  return undefined;
}
